import React, { useMemo, useState, useEffect, useContext } from "react"
import {
  RadioLabelDescriptionInput,
  RadioWithImageBg,
  LaunchModalBuilderSection,
} from "@components/configurator"
import LabeledRadioField from "./LabeledRadioField"
import ToggleWithImageField from "./ToggleWithImageField"
import ToggleField from "./ToggleField"
import TextField from "./TextField"
import LongTextField from "./LongTextField"
import SelectWithImageField from "./SelectWithImageField"

import { useUI } from "@components/ui/context"
import { useAddItemToCart } from "@lib/swell/storefront-data-hooks"
import { prepareVariantsWithOptions } from "@lib/swell/storefront-data-hooks/src/utils/product"
import { getVariant } from "@lib/swell/storefront-data-hooks/src/api/operations-swell"
import { LoadingDots } from "@components/ui"
import { OptionInput, ProductOption, Product } from "@lib/swell/storefront-data-hooks/src/types"
import { sidebarContext } from "@components/common/Layout"
import { appToast } from "@components/common/Toast"

export interface ProductProps {
  className?: string
  children?: any
  product: Product
  renderSeo?: boolean
  description?: string
  title?: string
}

export { FORM_ERROR, FORM_SUCCESS } from "@components/common/Form"
import { Form, FormProps } from "@components/common/Form"

interface ProductFormProps {
  product: string | any
  swellProduct: string | any
  variant: any
  setVariant: React.Dispatch<any>
}
export interface Selection extends OptionInput {
  id: string
  name: string
  value: string
  optionId: string
  variant: boolean
}

const ProductForm = ({ product, swellProduct, variant, setVariant }: ProductFormProps) => {
  const addItem = useAddItemToCart()
  const variants = useMemo(() => prepareVariantsWithOptions(product), [product])
  const { setOpenSidebar } = useContext(sidebarContext)

  const options = product?.options

  const defaultSelections: Selection[] = options.map((option: any) => {
    if (option.values?.length === 0) {
      // short text
      return {
        id: option.id,
        name: option.name,
        value: "",
        optionId: option.id,
        variant: false,
      }
    } else if (option.values?.length === 1) {
      // toggle
      return {
        id: option.values[0].id,
        name: option.name,
        value: "",
        optionId: option.id,
        variant: true,
      }
    } else {
      return {
        id: option.values[0].id,
        name: option.name,
        value: option.values[0].name,
        optionId: option.id,
        variant: true,
      }
    }
  })

  const initialValues: any = {}
  options.map((option: any) => {
    if (option.values.length === 0)
      // text field
      // initialValues[option.name] = ""
      return
    else if (option.values?.length === 1) {
      // toggle
      initialValues[option.name] = false
    } else {
      initialValues[option.name] = option.values[0].name
    }
  })

  async function setSelectedVariant() {
    const selectedVariant = await getVariant(swellProduct, selections)

    if (selectedVariant) {
      setVariant(selectedVariant)
    }
  }

  const [loading, setLoading] = useState(false)
  const [selections, setSelections] = useState(defaultSelections)
  const [productOptions, setProductOptions] = useState(options)

  function updateSelectionChange(name: string, optionValueId: string, value: string) {
    const selectionToUpdate = selections.find((selection) => {
      return selection.name == name
    })

    if (selectionToUpdate) {
      let swellValue
      if (typeof value === "string") {
        swellValue = value
      } else {
        swellValue = value ? name : ""
      }

      selectionToUpdate.value = swellValue
      selectionToUpdate.id = optionValueId

      setSelections(selections)
      setSelectedVariant()
    }
  }

  function updateTextFieldSelectionChange(name: string, optionId: string, value: string) {
    const selectionToUpdate = selections.find((selection) => {
      return selection.name == name
    })

    if (selectionToUpdate) {
      selectionToUpdate.value = value
      selectionToUpdate.id = optionId

      setSelections(selections)
      setSelectedVariant()
    }
  }

  function inputChangeHandler(option: ProductOption, value: string) {
    const { name, values } = option
    const id = values.find((optionValue) => optionValue.name == value)?.id ?? option.id
    const selectionToUpdate = selections.find((selection) => {
      return selection.name == name
    })

    if (selectionToUpdate) {
      selectionToUpdate.value = value
      selectionToUpdate.id = id

      setSelections(selections)
      setSelectedVariant()
    }
  }

  const addToCart = async () => {
    setLoading(true)

    try {
      await addItem(
        product.id,
        1,
        selections.filter((selection) => !!selection.value)
      )
      setOpenSidebar(true)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      appToast({ type: "error", message: `Unable to add product to cart. Error: ${err}` })
    }
  }

  const findSelected = (optionId: string) => {
    return selections.find((selection: any) => selection.optionId === optionId)
  }

  useEffect(() => {
    setSelectedVariant()
  }, [])

  return (
    <Form
      initialValues={initialValues}
      onSubmit={async (values) => {
        addToCart()
      }}
      className="mt-6"
    >
      <div className="space-y-10">
        {productOptions?.length > 0 &&
          productOptions?.map((option: ProductOption) => {
            switch (option.input_component_string) {
              case "radio_with_label_and_description":
                return (
                  <div key={option.id}>
                    <RadioLabelDescriptionInput
                      key={option.id}
                      option={option}
                      // eslint-disable-next-line
                      // @ts-ignore
                      selected={findSelected(option.id).value}
                      onChange={(event: any) => {
                        inputChangeHandler(option, event)
                      }}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "toggle":
                return (
                  <div key={option.id}>
                    <ToggleField
                      key={option.id}
                      option={option}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "toggle_with_image":
                return (
                  <div key={option.id}>
                    <ToggleWithImageField
                      key={option.id}
                      option={option}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "radio_with_image_bg":
                return (
                  <div key={option.id}>
                    <RadioWithImageBg
                      key={option.id}
                      option={option}
                      optionValues={option.values}
                      // eslint-disable-next-line
                      // @ts-ignore
                      selected={findSelected(option.id).value}
                      onChange={(event: any) => {
                        inputChangeHandler(option, event)
                      }}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "dropdown":
                return (
                  <div key={option.id}>
                    <LabeledRadioField
                      key={option.id}
                      option={option}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "text_field":
                return (
                  <div key={option.id}>
                    <TextField
                      key={option.id}
                      option={option}
                      selections={selections}
                      updateTextFieldSelectionChange={updateTextFieldSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "long_text_field":
                return (
                  <div key={option.id}>
                    <LongTextField
                      key={option.id}
                      option={option}
                      selections={selections}
                      updateTextFieldSelectionChange={updateTextFieldSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              case "select_with_image":
                return (
                  <div key={option.id}>
                    <SelectWithImageField
                      key={option.id}
                      option={option}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
              default:
                return (
                  <div key={option.id}>
                    <LabeledRadioField
                      key={option.id}
                      option={option}
                      updateSelectionChange={updateSelectionChange}
                    />
                    <div className="mt-2 px-4">
                      <LaunchModalBuilderSection optionId={option.id} />
                    </div>
                  </div>
                )
            }
          })}
      </div>
      <div className="mt-4">
        <button
          name="add-to-cart"
          disabled={loading}
          // onClick={addToCart}
          type="submit"
          className="flex max-w-xs flex-1 items-center justify-center rounded-md border border-transparent bg-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-primary-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50 sm:w-full"
        >
          Add to cart {loading && <LoadingDots />}
        </button>

        {/* <button
          type="button"
          className="ml-4 py-3 px-3 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-500"
        >
          <HeartIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
          <span className="sr-only">Add to favorites</span>
        </button> */}
      </div>
    </Form>
  )
}

export default ProductForm
